import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, getErrorMessage } from '../../api/apiClient';
import { getProject } from '../../api/projectsApi';
import { useAuth } from '../../auth/AuthContext';
import type { Money, ProjectDetail as ProjectDetailModel } from '../../types/project';
import { USER_ROLES } from '../../types/user';
import './Dashboard.css';

function formatStatus(status: string | null | undefined): string {
  return String(status || 'Unknown').replaceAll('_', ' ');
}

function formatMoney(money: Money | null | undefined): string | null {
  if (!money) {
    return null;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency,
  }).format(Number(money.amount));
}

function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentUser } = useAuth();
  const [project, setProject] = useState<ProjectDetailModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    if (!projectId) {
      setError('NOT_FOUND');
      setLoading(false);
      return undefined;
    }

    getProject(projectId)
      .then((response) => {
        if (active) {
          setProject(response);
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          const message = requestError instanceof ApiError && requestError.status === 404
            ? 'NOT_FOUND'
            : getErrorMessage(requestError, 'The project could not be loaded.');
          setError(message);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [projectId]);

  if (loading) {
    return <div className="dashboard-route-status" role="status">Loading project...</div>;
  }

  if (!project || error || !currentUser) {
    return (
      <section className="dashboard-page">
        <div className="dashboard-empty-state">
          <p className="eyebrow">Project unavailable</p>
          <h1>{error === 'NOT_FOUND' ? 'This project is not visible to the current account.' : 'The project could not be loaded.'}</h1>
          {error && error !== 'NOT_FOUND' && <p role="alert">{error}</p>}
          <Link className="dashboard-action" to="/dashboard/projects">Back to projects</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-page">
      <div className="dashboard-page-header">
        <div>
          <p className="eyebrow">{project.clientOrganization}</p>
          <h1>{project.name}</h1>
        </div>
        <span className={`status-pill status-${String(project.health || 'unknown').toLowerCase().replaceAll('_', '-')}`}>
          {formatStatus(project.health)}
        </span>
      </div>

      <div className="project-detail-grid">
        <article className="dashboard-panel project-summary-panel">
          <h2>Summary</h2>
          <p>{project.summary}</p>
          <dl className="detail-list">
            <div><dt>Phase</dt><dd>{project.phase}</dd></div>
            <div><dt>Status</dt><dd>{formatStatus(project.status)}</dd></div>
            <div><dt>Start</dt><dd>{project.startDate}</dd></div>
            <div><dt>Target</dt><dd>{project.targetDate}</dd></div>
            {project.budget && <div><dt>Budget</dt><dd>{formatMoney(project.budget)}</dd></div>}
          </dl>
        </article>

        <article className="dashboard-panel">
          <h2>Milestones</h2>
          <div className="stack-list">
            {(project.milestones || []).map((milestone) => (
              <div key={milestone.id} className="stack-list-row">
                <span>
                  <strong>{milestone.title}</strong>
                  <small>Due {milestone.dueDate}</small>
                </span>
                <span>{formatStatus(milestone.status)}</span>
              </div>
            ))}
            {(project.milestones || []).length === 0 && <p className="inline-empty-state">No milestones yet.</p>}
          </div>
        </article>

        <article className="dashboard-panel">
          <h2>Updates</h2>
          <div className="stack-list">
            {(project.updates || []).map((update) => (
              <div key={update.id} className="update-row">
                <span>{update.author?.name || 'Strata Team'} | {new Date(update.createdAt).toLocaleDateString()}</span>
                <p>{update.body}</p>
              </div>
            ))}
            {(project.updates || []).length === 0 && <p className="inline-empty-state">No updates yet.</p>}
          </div>
        </article>

        {(currentUser.role === USER_ROLES.ADMIN || currentUser.role === USER_ROLES.DEVELOPER) && (
          <article className="dashboard-panel">
            <h2>Internal tasks</h2>
            <div className="stack-list">
              {(project.tasks || []).map((task) => (
                <div key={task.id} className="stack-list-row">
                  <span>
                    <strong>{task.title}</strong>
                    <small>{task.assignee?.name || 'Unassigned'}</small>
                  </span>
                  <span>{task.status}</span>
                </div>
              ))}
              {(project.tasks || []).length === 0 && <p className="inline-empty-state">No internal tasks yet.</p>}
            </div>
          </article>
        )}
      </div>
    </section>
  );
}

export default ProjectDetail;
